import { useRef } from 'react';
import { SCENE_THUMBS } from '../game/PuzzlePicture.js';
import { useSettings } from '../settings.js';
import type { StringKey } from '../i18n/index.js';
import { useMedia } from '../media.js';

/**
 * Which picture a puzzle board hides.
 *
 * This used to be a dropdown of filenames, which is no help at all: a teacher
 * picking between `IMG-20260822-WA0050.jpg` and `IMG-20260822-WA0051.jpg` is
 * guessing. Every choice is shown as the thing it actually is, and a teacher
 * who has not uploaded anything yet can upload from right here rather than
 * being sent off to another panel and back.
 */
export function PicturePicker({ value, onChange }: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useSettings();
  const media = useMedia();
  const input = useRef<HTMLInputElement>(null);
  const mine = media.pictures;

  return (
    <div className="a-field">
      <span className="a-label">{t('admin.puzzleScene')}</span>

      <div className="pic-pick" role="radiogroup" aria-label={t('admin.puzzleScene')}>
        <Choice on={value === ''} onPick={() => onChange('')} label={t('admin.sceneRandom')}>
          {/* The dice face stands in for "a different one every board" — there
              is no single picture to show, which is the whole point. */}
          <span className="pic-any" aria-hidden="true">🎲</span>
        </Choice>

        {SCENE_THUMBS.map((s) => (
          <Choice key={s.name} on={value === s.name} onPick={() => onChange(s.name)} label={t(`scene.${s.name}` as StringKey)}>
            <img src={s.src} alt="" />
          </Choice>
        ))}

        {mine.map((p) => (
          <Choice key={p.id} on={value === p.id} onPick={() => onChange(p.id)} label={p.name}>
            <img src={p.src} alt="" />
          </Choice>
        ))}
      </div>

      {media.canUploadPictures && (
        <>
          <button
            type="button" className="a-reset" disabled={media.busy}
            onClick={() => input.current?.click()}
          >
            {media.busy ? t('media.uploading') : t('admin.sceneUpload')}
          </button>
          <input
            ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset first: picking the same file twice must fire again.
              e.target.value = '';
              if (!file) return;
              // Select what was just uploaded — a teacher who uploads a picture
              // from this field meant to use it.
              void media.upload('picture', file).then((id) => { if (id) onChange(id); });
            }}
          />
        </>
      )}
      {media.error && <p className="a-error">{t(`media.err.${media.error}` as StringKey)}</p>}
      <span className="a-hint">{t(value === '' ? 'admin.sceneRandomHelp' : 'media.pictureHelp')}</span>
    </div>
  );
}

function Choice({ on, onPick, label, children }: {
  on: boolean; onPick: () => void; label: string; children: React.ReactNode;
}) {
  return (
    <button
      type="button" role="radio" aria-checked={on} title={label}
      className={`pic-opt${on ? ' is-on' : ''}`}
      onClick={onPick}
    >
      {children}
      <span className="pic-name">{label}</span>
    </button>
  );
}
